import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Loading from '@components/v2/ui/Loading/Loading';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import type { Theme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { MouseEvent, SyntheticEvent, useState } from 'react';
import LangEnum from '../../../../../../../lang/LangEnum';
import { onScrollEndReloadForUseInfinityList } from '../../../../../../../utils/generics';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { CatalogViewOption } from '../../../view/catalogView.types';

interface CatalogViewRawOption<T> extends Omit<CatalogViewOption<T>, 'label'> {
  rawLabel: string;
}

type CatalogViewValueSelectOption<T> = CatalogViewOption<T> | CatalogViewRawOption<T>;

interface CatalogViewValueSelectInfiniteList {
  isLoading: boolean;
  hasNextPage: boolean;
  loadMoreRows: () => void;
}

interface CatalogViewValueSelectProps<T> {
  label: LangEnum;
  value: T;
  options: CatalogViewValueSelectOption<T>[];
  testId: string;
  disabled?: boolean;
  hasError?: boolean;
  infiniteList?: CatalogViewValueSelectInfiniteList;
  onChange: (value: T) => void;
}

const INFINITE_LIST_PAPER_SX = { maxHeight: 320 };

const TRIGGER_SX = { maxWidth: 106, minWidth: 0, paddingRight: 0.75 };

const ERROR_TRIGGER_SX = {
  ...TRIGGER_SX,
  border: '1px solid',
  borderColor: 'error.main',
  '& .MuiButton-endIcon': { color: 'error.main' },
};

const BLOCKED_OPTION_SX = (theme: Theme) => ({
  opacity: theme.palette.action.disabledOpacity,
  cursor: 'default',
});

const CatalogViewValueSelect = <T extends string | null>({
  label,
  value,
  options,
  testId,
  disabled,
  hasError,
  infiniteList,
  onChange,
}: CatalogViewValueSelectProps<T>) => {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const getLabel = (option: CatalogViewValueSelectOption<T>): string => {
    if ('rawLabel' in option) {
      return option.rawLabel;
    }
    return translate(option.label);
  };

  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption ? getLabel(selectedOption) : '';

  const handleSelect = (nextValue: T) => {
    onChange(nextValue);
    setAnchorEl(null);
  };

  const handleScroll = (event: SyntheticEvent) => {
    if (!infiniteList) {
      return;
    }

    onScrollEndReloadForUseInfinityList({
      event,
      hasNextPage: infiniteList.hasNextPage,
      loadMoreRows: infiniteList.loadMoreRows,
    });
  };

  const getAccessibleLabel = (option: CatalogViewValueSelectOption<T>) => {
    if (!option.blockedReason) {
      return undefined;
    }

    return `${getLabel(option)}, ${translate(option.blockedReason)}`;
  };

  const renderOption = (option: CatalogViewValueSelectOption<T>) => (
    <MenuItem
      key={String(option.value)}
      selected={option.value === value}
      aria-disabled={option.isDisabled || undefined}
      aria-label={getAccessibleLabel(option)}
      sx={option.isDisabled ? BLOCKED_OPTION_SX : undefined}
      onClick={() => {
        if (option.isDisabled) {
          return;
        }

        handleSelect(option.value);
      }}
    >
      <Tooltip
        fullWidth
        tabIndex={-1}
        TooltipContent={option.blockedReason ? translate(option.blockedReason) : undefined}
      >
        <Typography variant="body2" sx={{ px: 1.5, py: 0.5 }}>
          {getLabel(option)}
        </Typography>
      </Tooltip>
    </MenuItem>
  );

  const renderOptions = () => {
    if (options.length === 0) {
      return (
        <MenuItem
          disabled
          aria-label={infiniteList?.isLoading ? translate(LangEnum.GLOBAL_LOADING) : undefined}
        >
          {infiniteList?.isLoading ? (
            <Loading size={16} sx={{ mx: 1.5, my: 0.5 }} />
          ) : (
            <Typography variant="body2" sx={{ px: 1.5, py: 0.5 }}>
              {translate(LangEnum.GLOBAL_NO_OPTIONS)}
            </Typography>
          )}
        </MenuItem>
      );
    }

    return options.map((option) => renderOption(option));
  };

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="secondary"
        shape="square"
        sx={hasError ? ERROR_TRIGGER_SX : TRIGGER_SX}
        testId={testId}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-label={`${translate(label)}: ${selectedLabel}`}
        endIcon={<Icon icon={ChevronDown} size="sm" />}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
      >
        <Typography variant="inherit" component="span" noWrap title={selectedLabel}>
          {selectedLabel}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setAnchorEl(null)}
        slotProps={
          infiniteList
            ? { paper: { sx: INFINITE_LIST_PAPER_SX, onScroll: handleScroll } }
            : undefined
        }
      >
        {renderOptions()}
      </Menu>
    </>
  );
};

export default CatalogViewValueSelect;
